import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

// ============================================
// INTERFACES - OPÇÕES DE SELEÇÃO
// ============================================

export interface IReferenciaOption {
  value: number;
  label: string;
}

// ============================================
// INTERFACES - TIPOS DE DADOS
// ============================================

export interface ITipoAtividade {
  idTipoAtividade: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  ehTipologia?: boolean;
}

export interface IEmpresaTarefaReferencia {
  idEmpresaTarefaReferencia: number;
  idEmpresa: number;
  idReferencia: number;
  nomeReferencia: string;
  dataCriacao: string;
}

export interface IEmpresaGatilhoSubtarefa {
  idEmpresaGatilhoSubtarefa: number;
  idEmpresaTarefaGatilho: number;
  nome: string;

  ordem: number;
  ativo: boolean;
  dataCriacao: string;
}

export interface IEmpresaTarefaGatilho {
  idEmpresaTarefaGatilho: number;
  idEmpresa: number;
  idTipoAtividade: number;
  nomeTipoAtividade: string;
  descricaoTipoAtividade: string;
  ativo: boolean;
  dataCriacao: string;
  quantidadeSubtarefas: number;
  subtarefas: IEmpresaGatilhoSubtarefa[];
}

export interface IConfiguracaoTarefasEmpresa {
  idEmpresa: number;
  referencias: IEmpresaTarefaReferencia[];
  gatilhos: IEmpresaTarefaGatilho[];
}

// ============================================
// INTERFACES - REQUESTS
// ============================================

export interface ISalvarReferenciasRequest {
  idEmpresa: number;
  idReferencias: number[];
}

export interface ISalvarSubtarefaRequest {
  idEmpresaGatilhoSubtarefa?: number;
  nome: string;

  ordem: number;
  ativo: boolean;
}

export interface ISalvarGatilhoRequest {
  idEmpresaTarefaGatilho?: number;
  idEmpresa: number;
  idTipoAtividade: number;
  ativo: boolean;
  subtarefas: ISalvarSubtarefaRequest[];
}

export interface IAlterarStatusGatilhoRequest {
  idEmpresaTarefaGatilho: number;
  ativo: boolean;
}

export interface IReordenarSubtarefasRequest {
  idEmpresaTarefaGatilho: number;
  ordenacao: {
    idEmpresaGatilhoSubtarefa: number;
    novaOrdem: number;
  }[];
}

// ============================================
// SERVIÇOS - REFERÊNCIAS (SELEÇÃO)
// ============================================

/**
 * Obtém todas as referências para seleção (todas as referências do sistema)
 */
export const getReferenciasParaSelecao = async (): Promise<IReferenciaOption[]> => {
  try {
    const { data } = await api.get<IReferenciaOption[]>("/Tarefa/ObterReferenciasDisponiveis");
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar as referências");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém as referências liberadas para a empresa (Empresas > Tarefas >
 * Selecionar referências) já mapeadas para opções de seleção {value, label}
 */
export const getReferenciasEmpresaParaSelecao = async (
  idEmpresa: number
): Promise<IReferenciaOption[]> => {
  const referencias = await getReferenciasEmpresa(idEmpresa);
  return referencias.map((ref) => ({
    value: ref.idReferencia,
    label: ref.nomeReferencia,
  }));
};

// ============================================
// SERVIÇOS - TIPOS DE ATIVIDADE
// ============================================

/**
 * Obtém todos os tipos de atividade ativos
 */
export const getTiposAtividade = async (): Promise<ITipoAtividade[]> => {
  try {
    const { data } = await api.get<ITipoAtividade[]>("/Tarefa/ObterTiposAtividade");
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os tipos de atividade");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém os tipos de atividade configurados para a empresa
 * (apenas tipos com gatilho ativo no cadastro da empresa)
 */
export const getTiposAtividadeEmpresa = async (
  idEmpresa: number
): Promise<ITipoAtividade[]> => {
  try {
    const { data } = await api.get<ITipoAtividade[]>(
      `/Tarefa/ObterTiposAtividadeEmpresa/${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os tipos de atividade");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém todos os tipos de atividade (ativos e inativos) para tela de gerenciamento
 */
export const getTodosTiposAtividade = async (): Promise<ITipoAtividade[]> => {
  try {
    const { data } = await api.get<ITipoAtividade[]>("/Tarefa/ObterTodosTiposAtividade");
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os tipos de atividade");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Cria ou atualiza um tipo de atividade
 */
export const salvarTipoAtividade = async (
  tipoAtividade: Partial<ITipoAtividade>
): Promise<{ idTipoAtividade: number }> => {
  try {
    const { data } = await api.post<{ idTipoAtividade: number }>(
      "/Tarefa/SalvarTipoAtividade",
      tipoAtividade
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível salvar o tipo de atividade");
    throw error;
  }
};

/**
 * Exclui um tipo de atividade
 */
export const excluirTipoAtividade = async (id: number): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/Tarefa/ExcluirTipoAtividade/${id}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o tipo de atividade");
    throw error;
  }
};

/**
 * Altera o status (ativo/inativo) de um tipo de atividade
 */
export const alterarStatusTipoAtividade = async (
  id: number,
  ativo: boolean
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      `/Tarefa/AlterarStatusTipoAtividade?id=${id}&ativo=${ativo}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível alterar o status do tipo de atividade");
    throw error;
  }
};

// ============================================
// (bloco de Etapas removido na FASE 04 — decisão F04-5)

// ============================================
// SERVIÇOS - REFERÊNCIAS
// ============================================

/**
 * Obtém as referências de tarefas de uma empresa
 */
export const getReferenciasEmpresa = async (
  idEmpresa: number
): Promise<IEmpresaTarefaReferencia[]> => {
  try {
    const { data } = await api.get<IEmpresaTarefaReferencia[]>(
      `/Tarefa/ObterReferenciasEmpresa/${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar as referências");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Salva as referências de tarefas de uma empresa
 */
export const salvarReferenciasEmpresa = async (
  request: ISalvarReferenciasRequest
): Promise<boolean> => {
  try {
    const { data } = await api.post<boolean>(
      "/Tarefa/SalvarReferenciasEmpresa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível salvar as referências");
    throw error;
  }
};

// ============================================
// SERVIÇOS - GATILHOS
// ============================================

/**
 * Obtém os gatilhos de tarefas de uma empresa
 */
export const getGatilhosEmpresa = async (
  idEmpresa: number
): Promise<IEmpresaTarefaGatilho[]> => {
  try {
    const { data } = await api.get<IEmpresaTarefaGatilho[]>(
      `/Tarefa/ObterGatilhosEmpresa/${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os gatilhos");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém um gatilho específico por ID
 */
export const getGatilhoPorId = async (
  idEmpresaTarefaGatilho: number
): Promise<IEmpresaTarefaGatilho | null> => {
  try {
    const { data } = await api.get<IEmpresaTarefaGatilho>(
      `/Tarefa/ObterGatilhoPorId/${idEmpresaTarefaGatilho}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar o gatilho");
    return null;
  }
};

/**
 * Obtém um gatilho por tipo de atividade e empresa
 */
export const getGatilhoPorTipoAtividade = async (
  idEmpresa: number,
  idTipoAtividade: number
): Promise<IEmpresaTarefaGatilho | null> => {
  try {
    console.log("[getGatilhoPorTipoAtividade] Buscando gatilho:", { idEmpresa, idTipoAtividade });
    const { data } = await api.get<IEmpresaTarefaGatilho>(
      `/Tarefa/ObterGatilhoPorTipoAtividade?idEmpresa=${idEmpresa}&idTipoAtividade=${idTipoAtividade}`
    );
    console.log("[getGatilhoPorTipoAtividade] Resposta:", data);
    return data || null;
  } catch (error: any) {
    console.error("[getGatilhoPorTipoAtividade] Erro:", error);
    // Não exibir erro ao usuário se não encontrar gatilho (é esperado)
    if (error?.response?.status === 400 || error?.response?.status === 404) {
      console.log("[getGatilhoPorTipoAtividade] Gatilho não encontrado (esperado)");
      return null;
    }
    // Apenas logar outros erros, não interromper o fluxo
    console.warn("[getGatilhoPorTipoAtividade] Erro ao buscar gatilho:", error?.message);
    return null;
  }
};

/**
 * Salva/atualiza um gatilho de tarefa
 */
export const salvarGatilho = async (
  request: ISalvarGatilhoRequest
): Promise<{ idEmpresaTarefaGatilho: number }> => {
  try {
    const { data } = await api.post<{ idEmpresaTarefaGatilho: number }>(
      "/Tarefa/SalvarGatilho",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível salvar o gatilho");
    throw error;
  }
};

/**
 * Altera o status (ativo/inativo) de um gatilho
 */
export const alterarStatusGatilho = async (
  request: IAlterarStatusGatilhoRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/Tarefa/AlterarStatusGatilho",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível alterar o status do gatilho");
    throw error;
  }
};

/**
 * Exclui um gatilho e suas sub-tarefas
 */
export const excluirGatilho = async (
  idEmpresaTarefaGatilho: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/Tarefa/ExcluirGatilho/${idEmpresaTarefaGatilho}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o gatilho");
    throw error;
  }
};

// ============================================
// SERVIÇOS - SUB-TAREFAS
// ============================================

/**
 * Reordena as sub-tarefas de um gatilho
 */
export const reordenarSubtarefas = async (
  request: IReordenarSubtarefasRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/Tarefa/ReordenarSubtarefas",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível reordenar as sub-tarefas");
    throw error;
  }
};

/**
 * Exclui uma sub-tarefa específica
 */
export const excluirSubtarefa = async (
  idEmpresaGatilhoSubtarefa: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/Tarefa/ExcluirSubtarefa/${idEmpresaGatilhoSubtarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir a sub-tarefa");
    throw error;
  }
};

// ============================================
// SERVIÇOS - CONFIGURAÇÃO COMPLETA
// ============================================

/**
 * Obtém a configuração completa de tarefas de uma empresa
 */
export const getConfiguracaoTarefasEmpresa = async (
  idEmpresa: number
): Promise<IConfiguracaoTarefasEmpresa | null> => {
  try {
    const { data } = await api.get<IConfiguracaoTarefasEmpresa>(
      `/Tarefa/ObterConfiguracaoTarefasEmpresa/${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar a configuração de tarefas");
    return null;
  }
};

